import type { Content, TDocumentDefinitions } from 'pdfmake/interfaces.js';
import { formatDateTime } from '../../format.js';
import { safeFileBase } from '../drills/report-pdf.js';
import {
  pdfFacts,
  pdfHeading,
  pdfTable,
  pdfText,
  type RecordPdfT,
  recordPdf,
} from '../reports/record-pdf.js';
import type { ReportBrand } from '../voyage/source.js';
import type { CrewNote } from './crew-notes.js';
import type { CrewProfileView } from './types.js';

/**
 * Ofis notlarının dışa aktarımı (ADR 0159; KVKK m.11, GDPR md. 15): kişinin bütün notları,
 * geri çekilenler ve her notun bütün sürümleri (yazan, UTC zaman, tür, metin, gerekçe). Kişiye
 * verilmek üzere ofiste üretilir; pdfmake istemcide, internetsiz çalışır.
 */
export interface CrewNotesPdfInput {
  view: Pick<CrewProfileView, 'fullName' | 'profile'>;
  notes: readonly CrewNote[];
  brand: ReportBrand | null;
  /** Basım zamanı, UTC ISO. */
  printedAt: string;
}

const utc = (iso: string) => formatDateTime(iso, 0);

export function crewNotesPdf(t: RecordPdfT, input: CrewNotesPdfInput): TDocumentDefinitions {
  const p = (key: string, options?: Record<string, unknown>) => t(`crewNotes.pdf.${key}`, options);
  const k = (key: string, options?: Record<string, unknown>) => t(`crewNotes.${key}`, options);
  const { view, notes } = input;
  const content: Content[] = [
    pdfFacts([
      [p('person'), view.fullName],
      [p('crewCode'), pdfText(view.profile?.crewCode)],
      [p('count'), String(notes.length)],
    ]),
    { text: p('basis'), style: 'muted', margin: [0, 4, 0, 0] },
  ];
  if (notes.length === 0) {
    content.push({ text: p('none'), style: 'muted', margin: [0, 8, 0, 0] });
  }
  notes.forEach((note, index) => {
    const current = note.current;
    const marks = [k(`kind.${current.kind}`)];
    if (note.withdrawn) marks.push(k('meta.withdrawn'));
    content.push(
      pdfHeading(`${p('note', { index: index + 1 })} – ${marks.join(' · ')}`),
      pdfFacts([
        [
          p('columns.by'),
          `${note.createdByName ?? k('meta.unknownAuthor')} · ${utc(note.createdAt)}`,
        ],
        ...(current.vesselName
          ? ([[k('form.vessel'), current.vesselName]] as [string, string][])
          : []),
      ]),
      { text: current.body, margin: [0, 4, 0, 4] },
      pdfTable(
        [
          p('columns.version'),
          p('columns.change'),
          p('columns.by'),
          p('columns.at'),
          p('columns.type'),
          p('columns.text'),
          p('columns.reason'),
        ],
        [...note.versions]
          .sort((a, b) => a.versionNo - b.versionNo)
          .map((version) => [
            String(version.versionNo),
            k(`history.change.${version.change}`),
            version.createdByName ?? k('meta.unknownAuthor'),
            utc(version.createdAt),
            k(`kind.${version.kind}`),
            version.body,
            pdfText(version.reason),
          ]),
        [24, 54, 70, 62, 54, '*', 70],
      ),
    );
  });
  return recordPdf(t, {
    brand: input.brand,
    title: p('title'),
    number: [view.profile?.crewCode, view.fullName].filter(Boolean).join(' · '),
    marks: [p('confidential')],
    subject: view.fullName,
    content,
    approvals: null,
    printedAt: input.printedAt,
  });
}

/** "office-notes-C-045-Gizem-Koc.pdf". */
export function crewNotesFileName(view: Pick<CrewProfileView, 'fullName' | 'profile'>): string {
  const parts = ['office-notes', view.profile?.crewCode ?? '', view.fullName];
  return `${safeFileBase(parts.filter(Boolean).join('-'))}.pdf`;
}
